import { AlertTriangle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { MinuteHeatmap, MultiLineChart, Sparkline, VolumeBars } from "./components/MiniCharts";
import { ReportCards } from "./components/ReportCards";
import { Sidebar, Topbar } from "./components/Shell";
import { MoversTable, RepeatedUnusualTable, SectorCohortTable } from "./components/Tables";
import { loadStockPayload, loadSymbolDirectory } from "./lib/data";
import { compactNumber, marketFreshnessLabel, marketFreshnessTone, rowTone, signedPct } from "./lib/format";
import { buildReportCards } from "./lib/reports";
import type { RepeatedUnusualRow, SectorMetadataCoverage, StockPayload, StockPoint, StockRow } from "./lib/types";

type ReplayRow = {
  index: number;
  point: StockPoint;
};

type ScannerSetupFilter = "any" | "gap-up" | "above-vwap" | "near-high";

type ScannerFilters = {
  currentOnly: boolean;
  minHits: number;
  minLatestRvol: number;
  minChangePct: number;
  setup: ScannerSetupFilter;
  watchlist: string;
};

type ScannerPreset = {
  id: string;
  name: string;
  filters: ScannerFilters;
};

export const SCANNER_FILTERS_STORAGE_KEY = "kzg-stock-house.scannerFilters.v1";
export const SCANNER_PRESETS_STORAGE_KEY = "kzg-stock-house.scannerPresets.v1";
export const SCANNER_LINK_MODE = "rvol";

const DEFAULT_SCANNER_FILTERS: ScannerFilters = {
  currentOnly: true,
  minHits: 3,
  minLatestRvol: 3,
  minChangePct: -100,
  setup: "any",
  watchlist: ""
};

function isScannerSetupFilter(value: unknown): value is ScannerSetupFilter {
  return value === "any" || value === "gap-up" || value === "above-vwap" || value === "near-high";
}

function normalizeScannerFilters(value: unknown): ScannerFilters {
  if (!value || typeof value !== "object") return DEFAULT_SCANNER_FILTERS;
  const item = value as Partial<ScannerFilters>;
  return {
    currentOnly: typeof item.currentOnly === "boolean" ? item.currentOnly : DEFAULT_SCANNER_FILTERS.currentOnly,
    minHits: Number.isFinite(item.minHits) ? Number(item.minHits) : DEFAULT_SCANNER_FILTERS.minHits,
    minLatestRvol: Number.isFinite(item.minLatestRvol) ? Number(item.minLatestRvol) : DEFAULT_SCANNER_FILTERS.minLatestRvol,
    minChangePct: Number.isFinite(item.minChangePct) ? Number(item.minChangePct) : DEFAULT_SCANNER_FILTERS.minChangePct,
    setup: isScannerSetupFilter(item.setup) ? item.setup : DEFAULT_SCANNER_FILTERS.setup,
    watchlist: typeof item.watchlist === "string" ? formatWatchlistInput(item.watchlist) : DEFAULT_SCANNER_FILTERS.watchlist
  };
}

function parseWatchlistTickers(value: string): string[] {
  return Array.from(
    new Set(
      value
        .toUpperCase()
        .split(/[\s,;]+/)
        .map((ticker) => ticker.trim())
        .filter((ticker) => /^[A-Z0-9][A-Z0-9.-]{0,9}$/.test(ticker))
    )
  );
}

function formatWatchlistInput(value: string): string {
  return parseWatchlistTickers(value).join(", ");
}

function numberFromParam(params: URLSearchParams, key: string, fallback: number): number {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

function loadScannerFiltersFromUrl(): ScannerFilters | null {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("scanner") !== SCANNER_LINK_MODE) return null;
    return normalizeScannerFilters({
      currentOnly: params.get("current") !== "0",
      minHits: numberFromParam(params, "hits", DEFAULT_SCANNER_FILTERS.minHits),
      minLatestRvol: numberFromParam(params, "rvol", DEFAULT_SCANNER_FILTERS.minLatestRvol),
      minChangePct: numberFromParam(params, "chg", DEFAULT_SCANNER_FILTERS.minChangePct),
      setup: params.get("setup") ?? DEFAULT_SCANNER_FILTERS.setup,
      watchlist: params.get("watch") ?? DEFAULT_SCANNER_FILTERS.watchlist
    });
  } catch {
    return null;
  }
}

function loadScannerFilters(): ScannerFilters {
  const urlFilters = loadScannerFiltersFromUrl();
  if (urlFilters) return urlFilters;
  try {
    const raw = window.localStorage.getItem(SCANNER_FILTERS_STORAGE_KEY);
    return raw ? normalizeScannerFilters(JSON.parse(raw)) : DEFAULT_SCANNER_FILTERS;
  } catch {
    return DEFAULT_SCANNER_FILTERS;
  }
}

function saveScannerFilters(filters: ScannerFilters) {
  try {
    window.localStorage.setItem(SCANNER_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Local storage can be unavailable in restricted browser contexts; filters still work in memory.
  }
}

function normalizeScannerPreset(value: unknown): ScannerPreset | null {
  if (!value || typeof value !== "object") return null;
  const item = value as Partial<ScannerPreset>;
  if (!item.id || !item.name || typeof item.id !== "string" || typeof item.name !== "string") return null;
  return {
    id: item.id,
    name: item.name,
    filters: normalizeScannerFilters(item.filters)
  };
}

function loadScannerPresets(): ScannerPreset[] {
  try {
    const raw = window.localStorage.getItem(SCANNER_PRESETS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeScannerPreset).filter((preset): preset is ScannerPreset => Boolean(preset));
  } catch {
    return [];
  }
}

function saveScannerPresets(presets: ScannerPreset[]) {
  try {
    window.localStorage.setItem(SCANNER_PRESETS_STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // Presets are progressive enhancement; the scanner remains usable without storage.
  }
}

function buildScannerShareUrl(filters: ScannerFilters): string {
  const url = new URL(window.location.href);
  url.searchParams.set("scanner", SCANNER_LINK_MODE);
  url.searchParams.set("current", filters.currentOnly ? "1" : "0");
  url.searchParams.set("hits", String(filters.minHits));
  url.searchParams.set("rvol", String(filters.minLatestRvol));
  url.searchParams.set("chg", String(filters.minChangePct));
  url.searchParams.set("setup", filters.setup);
  const watchlist = formatWatchlistInput(filters.watchlist);
  if (watchlist) {
    url.searchParams.set("watch", watchlist);
  } else {
    url.searchParams.delete("watch");
  }
  url.hash = "scanners";
  return url.toString();
}

export function App() {
  const [payload, setPayload] = useState<StockPayload | null>(null);
  const [symbolDirectory, setSymbolDirectory] = useState<StockRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [selectedTicker, setSelectedTicker] = useState("NVDA");
  const [selectedMinuteIndex, setSelectedMinuteIndex] = useState<number | null>(null);
  const [scannerFilters, setScannerFilters] = useState<ScannerFilters>(loadScannerFilters);
  const [scannerPresets, setScannerPresets] = useState<ScannerPreset[]>(loadScannerPresets);
  const [activeScannerPresetId, setActiveScannerPresetId] = useState("");
  const [scannerPresetName, setScannerPresetName] = useState("");
  const [scannerShareStatus, setScannerShareStatus] = useState("Copy link");

  const load = () => {
    setError(null);
    setDirectoryError(null);
    loadStockPayload()
      .then(async (data) => {
        setPayload(data);
        try {
          const directory = await loadSymbolDirectory();
          setSymbolDirectory(directory.symbols);
        } catch (err) {
          setSymbolDirectory([]);
          setDirectoryError((err as Error).message);
        }
      })
      .catch((err: Error) => setError(err.message));
  };

  useEffect(load, []);
  useEffect(() => {
    setSelectedMinuteIndex(null);
  }, [selectedTicker]);
  useEffect(() => {
    saveScannerFilters(scannerFilters);
  }, [scannerFilters]);
  useEffect(() => {
    saveScannerPresets(scannerPresets);
  }, [scannerPresets]);
  useEffect(() => {
    setScannerShareStatus("Copy link");
  }, [scannerFilters]);

  const updateScannerFilters = (filters: ScannerFilters) => {
    setActiveScannerPresetId("");
    setScannerFilters(filters);
  };

  const applyScannerPreset = (id: string) => {
    const preset = scannerPresets.find((item) => item.id === id);
    if (!preset) {
      setActiveScannerPresetId("");
      return;
    }
    setActiveScannerPresetId(preset.id);
    setScannerPresetName(preset.name);
    setScannerFilters(preset.filters);
  };

  const saveScannerPreset = () => {
    const name = scannerPresetName.trim() || `Scan ${scannerPresets.length + 1}`;
    const existing = scannerPresets.find((item) => item.name.toLowerCase() === name.toLowerCase());
    const preset: ScannerPreset = {
      id: existing?.id ?? `preset-${Date.now()}`,
      name,
      filters: scannerFilters
    };
    setScannerPresets((current) => {
      const withoutExisting = current.filter((item) => item.id !== preset.id);
      return [...withoutExisting, preset].slice(-12).sort((a, b) => a.name.localeCompare(b.name));
    });
    setActiveScannerPresetId(preset.id);
    setScannerPresetName(name);
  };

  const deleteScannerPreset = () => {
    if (!activeScannerPresetId) return;
    setScannerPresets((current) => current.filter((item) => item.id !== activeScannerPresetId));
    setActiveScannerPresetId("");
    setScannerPresetName("");
  };

  const shareScannerLink = async () => {
    const link = buildScannerShareUrl(scannerFilters);
    window.history.replaceState(null, "", link);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setScannerShareStatus("Link copied");
      } else {
        setScannerShareStatus("Link ready");
      }
    } catch {
      setScannerShareStatus("Link ready");
    }
  };

  const allRows = useMemo(() => {
    const seen = new Set<string>();
    return [
      payload?.selected,
      ...(payload?.leaders ?? []),
      ...(payload?.laggards ?? []),
      ...(payload?.topVolume ?? []),
      ...(payload?.unusualVolume ?? []),
      ...(payload?.sectorProxies ?? []),
      ...(payload?.symbols ?? []),
      ...symbolDirectory
    ].filter(
      (row): row is NonNullable<typeof row> => {
        if (!row || seen.has(row.ticker)) return false;
        seen.add(row.ticker);
        return true;
      }
    );
  }, [payload, symbolDirectory]);
  const selected = selectedTicker
    ? (allRows.find((row) => row.ticker === selectedTicker) ?? null)
    : (payload?.selected ?? payload?.topVolume?.[0] ?? null);
  const selectedHasTrace = Boolean(selected?.points?.length);
  const selectedPoints = selected?.points ?? [];
  const activeMinuteIndex = selectedPoints.length
    ? Math.min(selectedPoints.length - 1, Math.max(0, selectedMinuteIndex ?? selectedPoints.length - 1))
    : null;
  const activeMinute = activeMinuteIndex === null ? null : selectedPoints[activeMinuteIndex];
  const replayRows = useMemo(() => {
    return selectedPoints
      .map((point, index) => ({ point, index }))
      .sort((a, b) => b.point.volume - a.point.volume)
      .slice(0, 6);
  }, [selected]);
  const summary = payload?.summary;
  const searchSuggestions = useMemo(() => allRows.slice(0, 500).map((row) => row.ticker), [allRows]);
  const topMovers = useMemo(() => {
    const leaders = payload?.leaders ?? [];
    const laggards = payload?.laggards ?? [];
    return [...leaders.slice(0, 7), ...laggards.slice(0, 5)];
  }, [payload]);
  const scannerWatchlist = useMemo(() => new Set(parseWatchlistTickers(scannerFilters.watchlist)), [scannerFilters.watchlist]);
  const filteredRepeated = useMemo(() => {
    return (payload?.repeatedUnusualVolume ?? []).filter((row) => {
      if (scannerWatchlist.size && !scannerWatchlist.has(row.ticker)) return false;
      if (scannerFilters.currentOnly && !row.currentSession) return false;
      if (row.occurrences < scannerFilters.minHits) return false;
      if ((row.latestRelativeVolume ?? 0) < scannerFilters.minLatestRvol) return false;
      if ((row.changePct ?? -999) < scannerFilters.minChangePct) return false;
      if (scannerFilters.setup === "gap-up" && (row.gapPct ?? 0) <= 0) return false;
      if (scannerFilters.setup === "above-vwap" && !row.aboveVwap) return false;
      if (scannerFilters.setup === "near-high" && !row.nearSessionHigh) return false;
      return true;
    });
  }, [payload, scannerFilters, scannerWatchlist]);
  const reports = useMemo(
    () => buildReportCards(payload, selected, activeMinute, filteredRepeated),
    [payload, selected, activeMinute, filteredRepeated]
  );

  return (
    <div className="app-shell">
      <Sidebar latestDate={payload?.tradeDate ?? null} />
      <main className="workspace">
        <Topbar
          latestDate={payload?.tradeDate ?? null}
          generatedAt={payload?.generatedAt ?? null}
          coverage={payload?.source?.coverage ?? "full_day_and_minute"}
          freshness={payload?.freshness}
          searchValue={selectedTicker}
          suggestions={searchSuggestions}
          directoryCount={symbolDirectory.length || payload?.symbols?.length || 0}
          onSearchChange={setSelectedTicker}
          onRefresh={load}
        />
        {error ? (
          <section className="notice">
            <AlertTriangle size={24} />
            <div>
              <strong>Payload unavailable</strong>
              <span>{error}</span>
            </div>
          </section>
        ) : null}
        {directoryError ? (
          <section className="notice compact">
            <AlertTriangle size={18} />
            <div>
              <strong>Symbol directory fallback</strong>
              <span>{directoryError}</span>
            </div>
          </section>
        ) : null}
        {payload?.freshness && payload.freshness.status !== "fresh" ? (
          <section className="notice freshness-notice">
            <AlertTriangle size={22} />
            <div>
              <strong>Market data {marketFreshnessLabel(payload.freshness).toLowerCase()}</strong>
              <span>
                Expected completed session {payload.freshness.expectedCompletedTradingDate}; latest minute file{" "}
                {payload.freshness.latestMinuteDate ?? "missing"}; latest day file {payload.freshness.latestDayDate ?? "missing"}.
              </span>
              {payload.remediation?.downloadCommand ? <code>{payload.remediation.downloadCommand}</code> : null}
            </div>
          </section>
        ) : null}

        <section className="kpi-strip" id="market-pulse">
          <Metric label="Total Symbols" value={compactNumber(summary?.symbols)} sub="active in minute file" />
          <Metric label="Total Volume" value={compactNumber(summary?.totalVolume)} sub="current session" tone="up" />
          <Metric label="Dollar Turnover" value={compactNumber(summary?.turnover, true)} sub="minute aggregate notional" tone="up" />
          <Metric
            label="Leaders"
            value={`${summary?.advancing ?? "-"} / ${summary?.declining ?? "-"}`}
            sub="advancing / declining"
          />
          <Metric label="Unusual Volume" value={compactNumber(summary?.unusualVolumeCount)} sub=">= 3.0x RVOL" />
        </section>

        <section className="dashboard-grid">
          <section className="panel breadth-panel">
            <div className="panel-header">
              <h2>Market Breadth (Minute)</h2>
              <div className="legend">
                <span className="green">Advancing</span>
                <span className="red">Declining</span>
                <span className="yellow">New Highs</span>
                <span className="blue">SPY Proxy</span>
              </div>
            </div>
            <MultiLineChart data={payload?.marketBreadth ?? []} />
          </section>

          <section className="panel heat-panel">
            <div className="panel-header">
              <h2>Minute Heatmap</h2>
              <span>trade % of peak 1-min volume</span>
            </div>
            <MinuteHeatmap data={payload?.minuteSeries ?? []} />
          </section>

          <aside className="panel inspector" id="minute-replay">
            <div className="panel-header">
              <h2>Selected Symbol</h2>
              <span>{selected?.ticker ?? "None"}</span>
            </div>
            {selected ? (
              <>
                <div className="quote">
                  <strong>{selected.ticker}</strong>
                  <span>{selected.price?.toFixed(2) ?? "-"}</span>
                  <em className={rowTone(selected.changePct)}>{signedPct(selected.changePct)}</em>
                </div>
                <Sparkline points={selectedPoints} selectedIndex={activeMinuteIndex} onSelect={setSelectedMinuteIndex} />
                <CandleDetail point={activeMinute} />
                <div className="stat-grid">
                  <Stat label="Open" value={selected.open?.toFixed(2) ?? "-"} />
                  <Stat label="High" value={selected.high?.toFixed(2) ?? "-"} />
                  <Stat label="Low" value={selected.low?.toFixed(2) ?? "-"} />
                  <Stat label="Prev Close" value={selected.previousClose?.toFixed(2) ?? "-"} />
                  <Stat label="VWAP" value={selected.vwap?.toFixed(2) ?? "-"} />
                  <Stat label="Range" value={signedPct(selected.rangePct)} />
                  <Stat label="RVOL" value={selected.relativeVolume ? `${selected.relativeVolume.toFixed(2)}x` : "-"} />
                  <Stat label="V / Prev" value={signedPct(selected.volumeVsPreviousPct)} />
                  <Stat label="Volume" value={compactNumber(selected.volume)} />
                  <Stat label="$ Volume" value={compactNumber(selected.turnover, true)} />
                </div>
                <MinuteReplay rows={replayRows} activeIndex={activeMinuteIndex} onSelect={setSelectedMinuteIndex} />
                <div className="flags">
                  <span>Price &gt; session open</span>
                  <span>{selectedHasTrace ? "Minute trace packed" : "Summary-only symbol"}</span>
                  <span>Local Massive source</span>
                </div>
              </>
            ) : (
              <div className="empty">{payload ? "No matching symbol in this payload." : "Run the data build."}</div>
            )}
          </aside>

          <section className="panel table-panel" id="symbols">
            <div className="panel-header">
              <h2>Top Movers</h2>
              <span>by session % change</span>
            </div>
            <MoversTable rows={topMovers} onSelect={setSelectedTicker} />
          </section>

          <section className="panel proxy-panel">
            <div className="panel-header">
              <h2>Sector Cohorts</h2>
              <span>{payload?.sectorMetadataCoverage?.metadataRows ?? 0} metadata rows</span>
            </div>
            <SectorMetadataCoverageStrip coverage={payload?.sectorMetadataCoverage} onSelect={setSelectedTicker} />
            <SectorCohortTable rows={payload?.sectorCohorts ?? []} onSelect={setSelectedTicker} />
          </section>

          <section className="panel scanner-panel" id="scanners">
            <div className="panel-header">
              <h2>Repeated RVOL Scanner</h2>
              <span>
                {filteredRepeated.length} / {payload?.repeatedUnusualVolume?.length ?? 0} names
              </span>
            </div>
            <ScannerControls
              filters={scannerFilters}
              presets={scannerPresets}
              activePresetId={activeScannerPresetId}
              presetName={scannerPresetName}
              onChange={updateScannerFilters}
              onApplyPreset={applyScannerPreset}
              onPresetNameChange={setScannerPresetName}
              onSavePreset={saveScannerPreset}
              onDeletePreset={deleteScannerPreset}
              shareStatus={scannerShareStatus}
              onShare={shareScannerLink}
              onReset={() => {
                setActiveScannerPresetId("");
                setScannerFilters(DEFAULT_SCANNER_FILTERS);
              }}
            />
            <RepeatedUnusualTable rows={filteredRepeated} onSelect={setSelectedTicker} />
          </section>

          <section className="panel volume-panel">
            <div className="panel-header">
              <h2>Volume At Minute</h2>
              <span>{compactNumber(summary?.totalVolume)} total</span>
            </div>
            <VolumeBars data={payload?.minuteSeries ?? []} />
          </section>

          <section className="panel reports-panel" id="reports">
            <div className="panel-header">
              <h2>Reports & Exports</h2>
              <span>copy or download portable briefs</span>
            </div>
            <ReportCards reports={reports} />
          </section>

          <section className="panel pipeline-panel" id="pipeline">
            <div className="panel-header">
              <h2>Data Pipeline Timeline</h2>
              <span>U.S. equity minute aggregate</span>
            </div>
            <div className="pipeline">
              {["File Ingest", "Parse & Validate", "Normalize", "Aggregate", "Quality Checks", "Publish"].map((step) => (
                <div key={step} className="pipe-step">
                  <i />
                  <strong>{step}</strong>
                </div>
              ))}
            </div>
            <div className="source-row">
              <span>Latest file</span>
              <strong>{payload?.source?.minuteFile?.split("/").pop() ?? "not built"}</strong>
              <span>Size</span>
              <strong>{compactNumber(payload?.source?.minuteBytes)}</strong>
            </div>
            <div className="freshness-row">
              <span>Expected</span>
              <strong>{payload?.freshness?.expectedCompletedTradingDate ?? "unchecked"}</strong>
              <span>Minute lag</span>
              <strong className={marketFreshnessTone(payload?.freshness)}>
                {payload?.freshness ? `${payload.freshness.minuteLagTradingDays} sessions` : "-"}
              </strong>
              <span>Day lag</span>
              <strong>{payload?.freshness ? `${payload.freshness.dayLagTradingDays} sessions` : "-"}</strong>
            </div>
            {payload?.remediation?.downloadCommand ? (
              <div className="remediation-row">
                <span>Backfill</span>
                <code>{payload.remediation.downloadCommand}</code>
                <span>Rebuild</span>
                <code>{payload.remediation.rebuildCommand}</code>
              </div>
            ) : null}
          </section>
        </section>
      </main>
    </div>
  );
}

function ScannerControls({
  filters,
  presets,
  activePresetId,
  presetName,
  onChange,
  onApplyPreset,
  onPresetNameChange,
  onSavePreset,
  onDeletePreset,
  shareStatus,
  onShare,
  onReset
}: {
  filters: ScannerFilters;
  presets: ScannerPreset[];
  activePresetId: string;
  presetName: string;
  onChange: (filters: ScannerFilters) => void;
  onApplyPreset: (id: string) => void;
  onPresetNameChange: (name: string) => void;
  onSavePreset: () => void;
  onDeletePreset: () => void;
  shareStatus: string;
  onShare: () => void;
  onReset: () => void;
}) {
  return (
    <div className="scanner-controls" aria-label="Repeated RVOL scanner filters">
      <label>
        <input
          checked={filters.currentOnly}
          type="checkbox"
          onChange={(event) => onChange({ ...filters, currentOnly: event.target.checked })}
        />
        Current session
      </label>
      <label>
        <span>Min hits</span>
        <input
          max={10}
          min={2}
          step={1}
          type="number"
          value={filters.minHits}
          onChange={(event) => onChange({ ...filters, minHits: Number(event.target.value) })}
        />
      </label>
      <label>
        <span>Min latest RVOL</span>
        <input
          max={20}
          min={1}
          step={0.5}
          type="number"
          value={filters.minLatestRvol}
          onChange={(event) => onChange({ ...filters, minLatestRvol: Number(event.target.value) })}
        />
      </label>
      <label>
        <span>Min % chg</span>
        <input
          max={100}
          min={-100}
          step={1}
          type="number"
          value={filters.minChangePct}
          onChange={(event) => onChange({ ...filters, minChangePct: Number(event.target.value) })}
        />
      </label>
      <label>
        <span>Setup</span>
        <select
          value={filters.setup}
          onChange={(event) => onChange({ ...filters, setup: event.target.value as ScannerSetupFilter })}
        >
          <option value="any">Any</option>
          <option value="gap-up">Gap up</option>
          <option value="above-vwap">Above VWAP</option>
          <option value="near-high">Near high</option>
        </select>
      </label>
      <label className="scanner-watchlist">
        <span>Watchlist</span>
        <input
          aria-label="Watchlist"
          placeholder="NVDA, PLTR"
          type="text"
          value={filters.watchlist}
          onBlur={(event) => onChange({ ...filters, watchlist: formatWatchlistInput(event.target.value) })}
          onChange={(event) => onChange({ ...filters, watchlist: event.target.value.toUpperCase() })}
        />
      </label>
      <label>
        <span>Preset</span>
        <select value={activePresetId} onChange={(event) => onApplyPreset(event.target.value)}>
          <option value="">Saved views</option>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Name</span>
        <input
          aria-label="Preset name"
          placeholder="Name"
          type="text"
          value={presetName}
          onChange={(event) => onPresetNameChange(event.target.value)}
        />
      </label>
      <button className="scanner-reset" type="button" onClick={onSavePreset}>
        Save preset
      </button>
      <button className="scanner-reset" type="button" disabled={!activePresetId} onClick={onDeletePreset}>
        Delete preset
      </button>
      <button className="scanner-reset" type="button" onClick={onShare}>
        {shareStatus}
      </button>
      <button className="scanner-reset" type="button" onClick={onReset}>
        Reset
      </button>
      <span className="scanner-save-state">Autosaved</span>
    </div>
  );
}

function Metric({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "up" | "down" }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong className={tone}>{value}</strong>
      <em>{sub}</em>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SectorMetadataCoverageStrip({
  coverage,
  onSelect
}: {
  coverage?: SectorMetadataCoverage;
  onSelect?: (ticker: string) => void;
}) {
  if (!coverage) return null;
  const symbolCoverage = coverage.symbolCoveragePct === null ? "-" : `${coverage.symbolCoveragePct.toFixed(2)}%`;
  const volumeCoverage = coverage.volumeCoveragePct === null ? "-" : `${coverage.volumeCoveragePct.toFixed(2)}%`;
  const unmapped = coverage.unmappedTopSymbols.length ? coverage.unmappedTopSymbols.join(", ") : "none";
  const targets = coverage.unmappedTargets ?? [];
  return (
    <div className="coverage-strip">
      <div>
        <span>Symbol map</span>
        <strong>
          {coverage.matchedSymbols.toLocaleString("en-US")} / {coverage.totalSymbols.toLocaleString("en-US")}
        </strong>
        <em>{symbolCoverage}</em>
      </div>
      <div>
        <span>Volume map</span>
        <strong>{compactNumber(coverage.matchedVolume)}</strong>
        <em>{volumeCoverage}</em>
      </div>
      {targets.length ? (
        <div className="metadata-targets" aria-label="Top unmapped metadata targets">
          <span>Top unmapped volume</span>
          <div>
            {targets.slice(0, 5).map((target) => (
              <button type="button" key={target.ticker} onClick={() => onSelect?.(target.ticker)}>
                <strong>{target.ticker}</strong>
                <em>{compactNumber(target.volume)}</em>
                <small>{target.relativeVolume ? `${target.relativeVolume.toFixed(2)}x` : "-"}</small>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p>Top unmapped volume: {unmapped}</p>
      )}
    </div>
  );
}

function CandleDetail({ point }: { point: StockPoint | null }) {
  if (!point) {
    return <div className="empty small">Select a packed minute to inspect candle detail.</div>;
  }
  const movePct = point.open ? ((point.close - point.open) / point.open) * 100 : null;
  return (
    <div className="candle-detail" aria-label="Selected minute candle detail">
      <div>
        <span>Selected Minute</span>
        <strong>{point.time}</strong>
      </div>
      <div>
        <span>Open</span>
        <strong>{point.open.toFixed(2)}</strong>
      </div>
      <div>
        <span>High</span>
        <strong>{point.high.toFixed(2)}</strong>
      </div>
      <div>
        <span>Low</span>
        <strong>{point.low.toFixed(2)}</strong>
      </div>
      <div>
        <span>Close</span>
        <strong className={rowTone(movePct)}>{point.close.toFixed(2)}</strong>
      </div>
      <div>
        <span>Volume</span>
        <strong>{compactNumber(point.volume)}</strong>
      </div>
    </div>
  );
}

function MinuteReplay({
  rows,
  activeIndex,
  onSelect
}: {
  rows: ReplayRow[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="replay">
      <div className="replay-header">
        <strong>Minute Replay</strong>
        <span>click to inspect candle</span>
      </div>
      {rows.length ? (
        <div className="replay-list">
          {rows.map(({ point, index }) => {
            const direction = point.close >= point.open ? "up" : "down";
            return (
              <button
                className={`replay-row ${activeIndex === index ? "active" : ""}`}
                key={`${point.time}-${point.volume}-${point.close}`}
                type="button"
                aria-pressed={activeIndex === index}
                aria-label={`Inspect ${point.time} close ${point.close.toFixed(2)}`}
                onClick={() => onSelect(index)}
              >
                <time>{point.time}</time>
                <span className={direction}>{point.close.toFixed(2)}</span>
                <em>{compactNumber(point.volume)}</em>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="empty small">Full minute trace is lazy-packed for watchlist and top-volume symbols only.</div>
      )}
    </div>
  );
}
